import { useMemo } from 'react';

import { css } from '@emotion/react';
import { responsive, useTheme } from '@wci-tech/core-ui';

import { getContainerStyles } from '@/utils';
import { STICKY_HEADER_SHADOW } from '@/components/constants';

export const useStyles = (scrolled: boolean, showMobileMenu: boolean = true) => {
  const { theme } = useTheme();

  return useMemo(
    () => ({
      fullHeaderWrapper: css`
        position: relative;
        width: 100%;
        z-index: ${theme.zIndex.core.above + 7};
        overflow-anchor: none;

        box-shadow: none;
        transition: box-shadow 180ms ease;

        transition:
          ${
            theme.transitions.createTransition({
              property: 'box-shadow',
              duration: '180',
              easing: 'ease',
            }).styles
          },
          @media (max-width: 1058px) {
          &.is-scrolled {
            box-shadow: ${STICKY_HEADER_SHADOW};
          }
        }

        /* Platform pages using <Header showNavbar={false} /> */
        &.is-scrolled.without-navbar {
          box-shadow: ${STICKY_HEADER_SHADOW};
        }

        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }

        @media (min-width: 1059px) {
          &.is-scrolled:not(.without-navbar) {
            height: var(--landing-header-flow-height, auto);
            min-height: var(--landing-header-flow-height, 0px);
          }
        }

        @media (max-width: 1058px) {
          position: sticky;
          top: 0;
          left: 0;
          right: 0;
          height: auto;
          min-height: 0;
          background-color: var(--common-color);
        }

        @media (max-width: 1058px) {
          box-shadow: none;
          transition: box-shadow 180ms ease;

          &.is-scrolled {
            box-shadow:
              0 1px 2px rgb(15 23 42 / 6%),
              0 4px 12px -3px rgb(15 23 42 / 10%);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }

        &.without-navbar {
          position: sticky;
          top: 0;
          left: 0;
          right: 0;
          height: auto;
          min-height: 0;
          background-color: var(--common-color);
        }

        &.without-navbar.entering-sticky {
          @media (min-width: 1059px) {
            & > [data-header-surface] {
              will-change: transform, opacity;
              animation: fullHeaderSlideDown 0.5s cubic-bezier(0.22, 1, 0.36, 1);
            }
          }
        }

        &.returning-to-top {
          @media (min-width: 1059px) {
            overflow-x: clip;

            & > [data-header-surface] {
              will-change: transform, opacity;
              animation: fullHeaderSlideDown 0.5s cubic-bezier(0.22, 1, 0.36, 1);
            }
          }
        }

        @media (prefers-reduced-motion: reduce) {
          &.without-navbar.entering-sticky > [data-header-surface],
          &.returning-to-top > [data-header-surface] {
            animation: none !important;
            will-change: auto;
          }
        }

        @keyframes fullHeaderSlideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }

          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `,

      headerSurface: css`
        width: 100%;
      `,

      header: css`
        width: 100%;
        background-color: var(--common-color);
        position: relative;
        z-index: ${theme.zIndex.core.above + 4};
      `,

      platformHeader: css`
        position: sticky;
        top: 0;
        width: 100%;
        z-index: ${theme.zIndex.core.above + 4};
        background-color: var(--common-color);
      `,

      headerBookADemo: css`
        --btn-text-hover-color: #f5f6fa;
        --btn-text-active-color: #f5f6fa;
      `,

      desktopLogo: css`
        ${responsive.create({
          display: {
            default: 'none',
            md: 'block',
          },
        })}
      `,

      mobileLogo: css`
        ${responsive.create({
          display: {
            default: 'block',
            md: 'none',
          },
        })}
      `,

      topSection: css`
        @media (min-width: 1059px) {
          display: ${scrolled && showMobileMenu ? 'none' : 'block'};
        }
      `,

      toggleContainer: css`
        display: flex;

        @media (max-width: 1058px) {
          display: none;
        }
      `,

      btnContainer: css`
        @media (max-width: 1058px) {
          display: none;
        }
      `,

      mobileControls: css`
        display: none;

        @media (max-width: 1058px) {
          display: flex;
          align-items: center;
        }
      `,

      desktopNavbarWrapper: css`
        @media (max-width: 1058px) {
          display: none;
        }
      `,

      container: css`
        ${getContainerStyles(theme)};

        ${responsive.create({
          paddingTop: {
            default: theme.spacing.get(12),
            md: theme.spacing.get(12),
            lg: theme.spacing.get(16),
          },
          paddingBottom: {
            default: theme.spacing.get(12),
            md: theme.spacing.get(12),
            lg: theme.spacing.get(16),
          },
        })}
      `,

      button: css`
        background: linear-gradient(234.66deg, #4054d1 -1.18%, #17ceca 112.11%);
      `,

      toggleWrapper: css`
        --thumb-size: 32px;
        --thumb-offset-start: 8px;
        --thumb-offset-end: 8px;
      `,

      track: css`
        --ts-bg-color: #bdbdbd;
        --ts-active-color: #343434;
      `,
    }),
    [theme, scrolled, showMobileMenu],
  );
};
